import { RangePropertiesType } from "@website/ui/range/range.types";
import clamp from "lodash-es/clamp";
import { useCallback, useEffect, useMemo, useRef } from "react";

export const useRange = (properties: RangePropertiesType) => {
  const { value, min, max, id, step, onChange } = properties;
  const isSingle = typeof value === "number";

  const rangeReference = useRef<HTMLDivElement>(null);

  const minValue = isSingle ? 0 : value[0];
  const maxValue = isSingle ? 0 : value[1];

  const stepItems = useMemo(() => {
    if (!step) {
      return [];
    }
    const items = Array.from({
      length: clamp(Math.round(max / step) + 1, 0, 100)
    })?.map((_, index) => ({
      out: index * step < minValue || index * step > maxValue,
      id: index
    }));

    return items;
  }, [step, max, minValue, maxValue]);

  const onMinChange = useCallback(
    (event: any) => {
      const value = Number(event.target.value);
      if (maxValue > value) {
        onChange(isSingle ? maxValue : [value, maxValue], "minOnChange");
      }
    },
    [isSingle, maxValue, onChange]
  );

  const onMaxChange = useCallback(
    (event: any) => {
      const value = Number(event.target.value);
      if (isSingle) {
        onChange(value, "maxOnChange");
      } else if (minValue < value) {
        onChange([minValue, value], "maxOnChange");
      }
    },
    [isSingle, minValue, onChange]
  );

  const getPercent = useCallback(
    (value: any) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  useEffect(() => {
    const minPercent = getPercent(minValue);
    const maxPercent = getPercent(maxValue);

    if (rangeReference.current) {
      rangeReference.current.style.width = `${maxPercent - minPercent}%`;
      const { direction } = getComputedStyle(rangeReference.current);
      if (direction === "rtl") {
        rangeReference.current.style.right = `${minPercent}%`;
      } else {
        rangeReference.current.style.left = `${minPercent}%`;
      }
    }
  }, [minValue, maxValue, getPercent]);

  // useEffect(() => {
  //   onChange(isSingle ? maxValue : [minValue, maxValue]);
  // }, [minValue, maxValue, onChange]);

  return {
    isSingle,
    minValue,
    maxValue,
    stepItems,
    minInputProperties: {
      id: `min_${id}`,
      max,
      min,
      step,
      type: "range",
      value: minValue,
      onChange: onMinChange
    },
    maxInputProperties: {
      id: `max_${id}`,
      max,
      min,
      step,
      type: "range",
      value: maxValue,
      onChange: onMaxChange
    },
    rangeProperties: {
      ref: rangeReference
    }
  };
};
